import Bowser from 'bowser';
import { EventKey, AdEventKey, PlaybackState, } from '../enums';
import { TelemetrySession } from '../../telemetry';
import { EventKey as TelemetryEventKey } from '../../telemetry/enums/event-key';
import { formatStreamRepresentations } from '../../telemetry/utils';
import { formatCapabilities } from './telemetry-utils';
import { StreamType as TelemetryStreamType } from '../../telemetry/enums/stream-type';
import { convertToTelemetryStreamType } from '../../telemetry/utils/stream-type-converter';
import { calculateLatency } from '../../adapter/latency-measurement/utils';
import { getDeviceCapabilities } from '../../capability';
export const WithTelemetry = (Base) => {
    return class PlayerWithEvent extends Base {
        #telemetrySessions = [];
        #lastPlayedAdId = null;
        #lastPlayedAdPosition = null;
        dispatchMedia(media, noEvent) {
            const options = this._playOptions;
            const psid = this.psid;
            if (!options || !psid) {
                throw new Error('Something went wrong');
            }
            const telemetrySession = this.getTelemetrySession(psid);
            if (options.assetInfo.telemetryEndpoint) {
                telemetrySession?.setEndpoint(options.assetInfo.telemetryEndpoint);
            }
            else {
                telemetrySession?.disableTelemetry();
            }
            return super.dispatchMedia(media, noEvent);
        }
        async createStatusProperties() {
            const bufferInfo = await this.adapter.getBufferInformation();
            const bufferDuration = bufferInfo
                ? ((bufferInfo.videoForward ?? 0) +
                    (bufferInfo.videoBackward ?? 0) +
                    (bufferInfo.audioForward ?? 0) +
                    (bufferInfo.audioBackward ?? 0))
                : -1;
            const statusProperties = {
                buf_dur: Math.round(bufferDuration),
                buf_dur_forward: Math.round(bufferInfo?.videoForward ?? -1),
                cur_media_time: Math.round(this.currentPosition ?? 0) * 1_000,
                cur_bitrate: this._bitrate ?? -1,
                playback_window_start: Math.round(this.seekableRange?.start ?? 0),
                playback_window_end: Math.round(this.seekableRange?.end ?? 0),
                net_dl_speed_est: Math.round(this.bandwidth ?? 0),
                cpu_usage: this.adapter.getSystemStats()?.cpuUsage,
                mem_free: this.adapter.getSystemStats()?.memoryFree,
            };
            return statusProperties;
        }
        createTelemetrySession = (psid) => {
            // Terrible hack to get the appId from the zapi playerOptions
            const appId = this._playerOptions.hasOwnProperty('appId')
                ? this._playerOptions.appId
                : undefined;
            const telemetrySession = new TelemetrySession(() => {
                return this.createStatusProperties();
            }, {
                psid,
                appId,
                appName: this._playerOptions.appName,
                appVersion: this._playerOptions.appVersion,
                publicId: this._playerOptions.publicId,
                logger: this._logger,
            });
            this.#telemetrySessions.push(telemetrySession);
            return telemetrySession;
        };
        getTelemetrySession = (psid) => {
            const telemetrySession = this.#telemetrySessions.find((session) => session.psid === psid);
            return telemetrySession;
        };
        appendStreamLoadedEvent = (psid) => {
            const telemetrySession = this.getTelemetrySession(psid);
            telemetrySession?.appendEvent(async () => {
                const representations = await this.adapter.getAvailableRepresentations();
                return {
                    type: TelemetryEventKey.STREAM_LOADED,
                    psid: telemetrySession.psid,
                    representations: formatStreamRepresentations(representations),
                };
            });
        };
        triggerTelemetryNewSessionEvent(psid, assetType, assetId, streamType, streamId, streamProvider) {
            const telemetrySession = this.createTelemetrySession(psid);
            telemetrySession?.appendEvent(async () => {
                const [capabilities, deviceInfo, bufferInfo, geometry,] = await Promise.all([
                    getDeviceCapabilities(),
                    this.adapter.getDeviceInfo(),
                    this.adapter.getBufferInformation(),
                    this.adapter.getGeometry(),
                ]);
                // put into capability module with victors optimizely PR
                const getWebDeviceInfo = () => {
                    if (typeof window === 'undefined' || (
                    // eslint-disable-next-line
                    !window?.navigator?.userAgent &&
                        // @ts-expect-error incoplete types
                        // eslint-disable-next-line
                        !window?.navigator?.userAgentData)) {
                        return null;
                    }
                    const browser = Bowser.getParser(window.navigator.userAgent, 
                    // @ts-expect-error incoplete types
                    // eslint-disable-next-line
                    window.navigator.userAgentData);
                    const browserVersion = browser.getBrowserVersion();
                    let browserName = browser.getBrowserName();
                    if (browserVersion) {
                        browserName += ` ${browserVersion}`;
                    }
                    return {
                        browser_name: browserName,
                        device_os: browser.getOSName(),
                        device_os_ver: browser.getOSVersion(),
                        device_name: window.navigator.userAgent,
                    };
                };
                return {
                    type: TelemetryEventKey.NEW_SESSION,
                    psid,
                    player_name: this.adapter.playerName,
                    player_ver: this.adapter.playerVersion,
                    buf_player_conf_min_buf: Math.round(bufferInfo?.minimumVideoBufferForward ?? -1),
                    asset_type: assetType,
                    asset_id: assetId,
                    stream_type: streamType ? convertToTelemetryStreamType(streamType) : TelemetryStreamType.UNKNOWN,
                    player_size_w: geometry.playerWidth ?? 0,
                    player_size_h: geometry.playerHeight ?? 0,
                    screen_size_w: geometry.screenWidth ?? 0,
                    screen_size_h: geometry.screenHeight ?? 0,
                    capabilities: formatCapabilities(capabilities),
                    device_name: deviceInfo?.deviceName,
                    device_firmware: deviceInfo?.deviceFirmware,
                    device_os: deviceInfo?.deviceOs,
                    device_os_ver: deviceInfo?.deviceOsVersion,
                    ...getWebDeviceInfo(),
                    ...(streamProvider ? { stream_provider_id: streamProvider } : {}),
                    ...(streamId ? { stream_id: streamId } : {}),
                };
            });
        }
        triggerTelemetryEvent(payload) {
            const telemetrySession = this.getTelemetrySession(payload.psid);
            telemetrySession?.appendEvent(payload);
        }
        dispatchCustomTelemetryEvent(event) {
            const { type, psid, csid, ...payload } = event;
            if (csid) {
                const telemetrySession = this.getTelemetrySession(psid);
                telemetrySession?.setCsid(csid);
            }
            this.triggerTelemetryEvent({
                type: TelemetryEventKey.CUSTOM,
                customType: type,
                psid,
                csid,
                payload,
            });
        }
        evaluateEvent = (event) => {
            switch (event.type) {
                case EventKey.PLAYER_READY: {
                    this.appendStreamLoadedEvent(event.psid);
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.READY,
                        psid: event.psid,
                    });
                    break;
                }
                case EventKey.PLAYBACK_STATE_CHANGED: {
                    switch (event.state) {
                        case PlaybackState.PLAYING: {
                            this.triggerTelemetryEvent({
                                type: TelemetryEventKey.PLAYING,
                                psid: event.psid,
                            });
                            break;
                        }
                        case PlaybackState.BUFFERING: {
                            this.triggerTelemetryEvent({
                                type: TelemetryEventKey.BUFFERING,
                                psid: event.psid,
                            });
                            break;
                        }
                        case PlaybackState.PAUSED: {
                            this.triggerTelemetryEvent({
                                type: TelemetryEventKey.PAUSED,
                                psid: event.psid,
                            });
                            break;
                        }
                        default: {
                            break;
                        }
                    }
                    break;
                }
                case EventKey.QUALITY_CHANGED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.QUALITY_CHANGED,
                        psid: event.psid,
                    });
                    break;
                }
                case EventKey.SEEKED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.SEEKED,
                        psid: event.psid,
                    });
                    break;
                }
                case EventKey.SELECTED_AUDIO_TRACK_CHANGED: {
                    // session audio teardown event can be matched to wrong session
                    if (!event.targetTrack?.locale) {
                        return;
                    }
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.TRACK_AUDIO_CHANGED,
                        psid: event.psid,
                        language: event.targetTrack?.locale,
                        codec: event.targetTrack?.codec,
                    });
                    break;
                }
                case EventKey.SELECTED_SUBTITLES_TRACK_CHANGED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.TRACK_SUBTITLES_CHANGED,
                        psid: event.psid,
                        language: event.targetTrack?.locale,
                    });
                    break;
                }
                case EventKey.PLAYER_ERROR: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.ERROR,
                        psid: event.psid,
                        err_type: String(event.error.code),
                        err_payload: event.error.stack
                            ? `${event.error.message}\n${event.error.stack}`
                            : event.error.message,
                    });
                    break;
                }
                case EventKey.LATENCY_CHANGED: {
                    const latency = calculateLatency(event.metadata);
                    // todo: apple sdk can report wrong metadata on VOD streams
                    if (!latency || isNaN(latency)) {
                        return;
                    }
                    const telemetrySession = this.getTelemetrySession(event.psid);
                    // todo: android can report latency without csid
                    // on fast channel switching
                    if (!telemetrySession?.csid) {
                        return;
                    }
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.LATENCY,
                        psid: event.psid,
                        latency_ms: Math.floor(latency * 1000),
                    });
                    break;
                }
                case EventKey.DROPPED_FRAMES: {
                    const telemetrySession = this.getTelemetrySession(event.psid);
                    // start session with new session event
                    if (!telemetrySession || telemetrySession.sequence === 0) {
                        return;
                    }
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.DROPPED_FRAMES,
                        psid: event.psid,
                        dropped_frames: event.droppedFrames,
                        elapsed_ms: event.elapsedMs,
                    });
                    break;
                }
                case AdEventKey.AD_BREAK_STARTED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_REQUESTED,
                        psid: event.psid,
                    });
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_BREAK_START,
                        psid: event.psid,
                    });
                    break;
                }
                case AdEventKey.AD_BREAK_ENDED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_BREAK_END,
                        psid: event.psid,
                    });
                    break;
                }
                case AdEventKey.AD_LOADED: {
                    this.#lastPlayedAdId = event.data.adInfo.adId;
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_LOADED,
                        psid: event.psid,
                        ad_id: event.data.adInfo.adId,
                        ad_dur_ms: event.data.adInfo.duration * 1000,
                    });
                    break;
                }
                case AdEventKey.AD_STARTED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_STARTED,
                        psid: event.psid,
                        ad_id: event.data.adInfo.adId,
                        ad_type: event.data.adInfo.contentType,
                        ad_bitrate: event.data.adInfo.vastMediaBitrate * 1000,
                        // TODO(557)
                        ad_url: '',
                        ad_width: event.data.adInfo.width ?? 0,
                        ad_height: event.data.adInfo.height ?? 0,
                    });
                    break;
                }
                case AdEventKey.AD_PAUSED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_PAUSED,
                        psid: event.psid,
                        ad_id: this.#lastPlayedAdId ?? '',
                    });
                    break;
                }
                case AdEventKey.AD_ENDED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_END,
                        psid: event.psid,
                        ad_id: event.data.adInfo.adId,
                    });
                    break;
                }
                case AdEventKey.AD_POSITION_CHANGED: {
                    this.#lastPlayedAdPosition = event.data.position;
                    break;
                }
                case AdEventKey.AD_SKIPPED: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_SKIPPED,
                        psid: event.psid,
                        ad_id: this.#lastPlayedAdId ?? '',
                        ad_cur_media_time_ms: (this.#lastPlayedAdPosition ?? 0) * 1000,
                    });
                    break;
                }
                case AdEventKey.AD_ERROR: {
                    this.triggerTelemetryEvent({
                        type: TelemetryEventKey.AD_ERROR,
                        psid: event.psid,
                        ad_id: this.#lastPlayedAdId ?? '',
                        ad_err_code: String(event.data.error.code),
                        ad_err_payload: event.data.error.message,
                    });
                    break;
                }
                default: {
                    break;
                }
            }
        };
        triggerFullEvent(event, options) {
            // consider to defer this off the main event loop
            this.evaluateEvent(event);
            return super.triggerFullEvent(event, options);
        }
        async stop() {
            const psid = this.psid;
            await super.stop();
            if (psid) {
                const telemetrySession = this.getTelemetrySession(psid);
                telemetrySession?.close().catch((err) => {
                    this._logger?.error(err);
                });
            }
        }
        destroy() {
            const closePromises = this.#telemetrySessions.map((session) => {
                return session.close();
            });
            // @todo We need a reliable way to send telemetry events when app goes inactive
            // https://zattoo2.atlassian.net/browse/PLY-1872
            Promise.all(closePromises);
            this.#telemetrySessions = [];
            return super.destroy();
        }
    };
};
